import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserDataService } from '../../shared/services/userData.service';
import { PrvNextButtonsComponent } from '../components/prv-next-buttons/prv-next-buttons.component';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SummaryService } from '../../shared/services/eopSummary.service';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { Icons } from '../../shared/helpers/icons';
import { LucideAngularModule } from 'lucide-angular';
import { EopDocumentService } from '../../shared/services/eopDocument.service';
import { EopNavigationService } from '../../shared/services/eopNavigation.service';
import { DateUtils } from '../../shared/helpers/dateUtils';

@Component({
  selector: 'summary',
  standalone: true,
  imports: [
    LucideAngularModule,
    CommonModule,
    PrvNextButtonsComponent,
    SpinnerComponent
  ],
  templateUrl: './summary.component.html',
  styleUrl: './summary.component.css'
})
export class SummaryComponent implements OnInit {
  currentRoute = "summary";

  icons = Icons;
  currentBuildingId: string | null = null;
  selectedSchoolYear: string = '';

  loading: boolean = true; 

  summary: { [stepName: string]: {
    section: string;
    last_updated_at: string;
    updated_by: number;
    last_updated_user_name: string;
  }[] } = {};
  

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private userDataService: UserDataService,
    private summaryService: SummaryService,
    private eopDocumentService: EopDocumentService,
    public eopNavigationService: EopNavigationService
  ) { }

  ngOnInit(): void {          
    if (isPlatformBrowser(this.platformId)) { // CSR
      if (localStorage.getItem("rems_eop_building_id")) { // get building Id from local storage
        this.currentBuildingId = localStorage.getItem("rems_eop_building_id");
        this.userDataService.setBuildingId(this.currentBuildingId + ""); // set building Id from local storage
      }
    }
    this.currentBuildingId = this.userDataService.getBuildingId();
    if (this.currentBuildingId) {      
      this.eopNavigationService.loadTotalPages(this.currentBuildingId);
    }
    this.selectedSchoolYear = DateUtils.getCurrentSchoolYear();
    this.loadSummary();
    this.loadUserInfo();
  }

  loadUserInfo(): void {
    if (this.currentBuildingId) {
      this.userDataService.getUserInfo(this.currentBuildingId).subscribe({
        next: (response) => {
          if (!response.has_error) {
            const userData = response.data;
            this.userDataService.setUserData(userData); // Store user data in the service
            console.log('User info loaded successfully:', userData);
          } else {
            console.error('Error fetching user info:', response.message);
          }
        },
        error: (error) => {
          console.error('Error fetching user info:', error);
        }
      });
    }
  }

  loadSummary(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.summaryService.getEoPSummary(this.currentBuildingId, this.selectedSchoolYear).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.summary = response.data; // Assign the summary data to the component property
            console.log('Summary loaded successfully:', this.summary);
          } else {
            console.error('Error fetching summary:', response.message);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching summary:', error);
        }
      });
    } 
  }

  getStatusIcon(last_updated_at: string | null | undefined): 'incomplete' | 'complete' | 'attention' {
    if (!last_updated_at) return 'incomplete';
  
    const updatedDate = new Date(last_updated_at);
    const now = new Date();
    // Get July 1st of the current year
    const julyFirst = new Date(now.getFullYear(), 6, 1); // month is 0-based, so 6 = July

    // If today is before July 1st, use July 1st of the previous year as the reset point
    if (now < julyFirst) {
      julyFirst.setFullYear(julyFirst.getFullYear() - 1);
    }

    // If the update was on or after the last reset date (July 1), it's "complete"
    return updatedDate >= julyFirst ? 'complete' : 'attention';
  }

  downloadEop(): void {
    if (this.currentBuildingId) {
      this.loading = true;
  
      this.eopDocumentService.downloadEopDocument(this.currentBuildingId, this.selectedSchoolYear).subscribe({
        next: (response) => {
          this.loading = false;
  
          if (response.has_error || !response.data?.file_content) {
            console.error('No PDF content received');
            return;
          }
  
          const { file_content, filename } = response.data;
  
          // Decode base64 into binary
          const byteCharacters = atob(file_content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);

          // Blob with correct type (zip)
          const blob = new Blob([byteArray], { type: 'application/zip' });
  
          // Create and click download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename || `Emergency_Operations_Plan.zip`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error) => {
          this.loading = false;
          console.error('Error downloading EOP document:', error);
        }
      });
    }
  }
  
  
  
}
